<template>
  <div class="d-flex">
    <div
      v-if="!hideLoader && loading"
      class="loading d-flex flex-column align-center my-5"
    >
      <h3 class="text-h4 mb-3">
        {{ searchStatus }}
      </h3>
      <v-progress-circular indeterminate></v-progress-circular>
    </div>
    <v-alert v-if="noImageFound" border="left" colored-border type="error">
      <div class="title">
        {{ $t("visualizator.empty") }}
        </div>
      <div>
        <p>
          {{ $t("visualizator.endpointLabel") }} <code>{{ serverUrl }}</code>
          <br />
          {{ $t("visualizator.identifierLabel") }} <code>{{ studyUid }}</code>
        </p>
      </div>
    </v-alert>
    <div class="" ref="dicomImage" id="dicomImage" :style="style"></div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { api as dicomWeb } from "dicomweb-client/build/dicomweb-client";
import { VolumeImageViewerExt } from "@/viewer/viewer";
import { Control } from "ol/control";

export default {
  props: {
    hideLoader: { type: Boolean, default: false },
    height: { type: Number, default: null },
    serverUrl: { type: String, required: true },
    studyUid: { type: String, required: true },
    seriesUid: { type: String, required: true },
    floatMode: Boolean,
  },
  data: () => ({
    dicomClient: null,
    loading: false,
    searchStatus: null,
    noImageFound: false,
    viewer: null
  }),
  computed: {
    ...mapState({ token: state => state.authentication.keycloak.token }),
    searchOptions() {
      const studyInstanceUID = this.studyUid; //this.imagingStudy.identifier[0].value.substr(8);
      const seriesInstanceUID = this.seriesUid; //this.imagingStudy.series[0].uid;
      return {
        studyInstanceUID,
        seriesInstanceUID
      };
    },
    searching() {
      return this.$t("visualizator.searchStatus.searching");
    },
    retrieving() {
      return this.$t("visualizator.searchStatus.retrieving");
    },
    rendering() {
      return this.$t("visualizator.searchStatus.rendering");
    },
    style() {
      if (this.height) {
        return { height: `${this.height}px` };
      }
      return {};
    }
  },
  watch: {
    floatMode(val) {
      this.resize()
    }
  },
  methods: {
    async createDicomClient() {
      const headers = {
        Authorization: `Bearer ${this.token}`
      };
      this.dicomClient = new dicomWeb.DICOMwebClient({
        url: this.serverUrl,
        headers
      });
    },
    resize() {
      if (this.viewer) {
        let width = document.getElementById("dicomImage").clientWidth;
        const height = document.getElementById("dicomImage").clientHeight;
        this.viewer.map.getView().setViewportSize([width, height]);
        this.viewer.map.setSize([width, height]);
      }
    }
  },
  async mounted() {
    this.loading = true;
    await this.createDicomClient();
    this.searchStatus = this.searching;
    this.$emit("loading:search");
    let instances = await this.dicomClient.searchForInstances(
      this.searchOptions
    );
    const promises = [];
    this.searchStatus = this.retrieving;
    this.$emit("loading:metadata");
    const length = instances.length;
    if (length === 0) {
      // this.loading = false;
      this.noImageFound = true;
      this.$emit("slide-not-found");
      return;
    }

    for (let i = 0; i < length; i++) {
      const sopInstanceUID = instances[i]["00080018"]["Value"][0];

      const retrieveInstanceOptions = {
        ...this.searchOptions,
        sopInstanceUID
      };

      const promise = this.dicomClient
        .retrieveInstanceMetadata(retrieveInstanceOptions)
        .then(metadata => {
          const imageType = metadata[0]["00080008"]["Value"];
          if (imageType[2] === "VOLUME") {
            return metadata[0];
          }
        });
      promises.push(promise);
    }

    let metadata = await Promise.all(promises);
    this.searchStatus = this.rendering;

    metadata = metadata.filter(m => m);
    // Note: We are using retrieveRendered: false because dcm4chee does not yet
    // support this functionality. See https://github.com/dcm4che/dcm4chee-arc-light/issues/1617
    this.viewer = new VolumeImageViewerExt({
      client: this.dicomClient,
      metadata,
      retrieveRendered: false,
      controls: ["overview"]
    });

    // add custom control to show current zoom level
    const ctrl = (function(Control) {
      let label = document.createElement("span");

      function ZoomLevelControl(options) {
        updateHtml(options.map.getView().getZoom());
        const element = document.createElement("div");
        element.className = "label-zoom-level";
        element.appendChild(label);
        Control.call(this, { element, target: options.target });
      }

      function updateHtml(level) {
        console.log(level)
        label.innerHTML = `Magnification: ${Math.round(
          (level + Number.EPSILON) * 100
        ) / 100}x`;
      }

      if (Control) ZoomLevelControl.__proto__ = Control;
      ZoomLevelControl.prototype = Object.create(Control && Control.prototype);
      ZoomLevelControl.prototype.constructor = ZoomLevelControl;

      ZoomLevelControl.prototype.update = function update() {
        updateHtml(
          this.getMap()
            .getView()
            .getZoom()
        );
      };
      return ZoomLevelControl;
    })(Control);

    const zoomLvlCtrl = new ctrl({ map: this.viewer.map });
    this.viewer.map.addControl(zoomLvlCtrl);
    this.viewer.map.on("moveend", e => {
      this.$emit("map-move-end", e);
      zoomLvlCtrl.update();
    });

    // set collapsible = true and collapsed = true to avoid rendering blank overview map
    // due to a bug in openlayers (fix merged here but not yet in release: https://github.com/openlayers/openlayers/pull/12028)
    // so until now the user needs to toggle the overview map by hand to show it
    this.viewer.overviewMapControl.setCollapsible(true);
    this.viewer.overviewMapControl.setCollapsed(true);
    this.viewer.map.once(["postrender"], _ => {
      this.viewer.overviewMapControl.setCollapsed(false);
    })

    const container = this.$refs.dicomImage;

    this.viewer.render({ container });
    this.loading = false;
    this.$emit("loading:rendered");
  },

  beforeDestroy() {
    this.viewer = null;
  }
};
</script>

<style lang="scss" scoped>
#dicomImage {
  height: 100%;
  width: 100%;
}

.label-zoom-level {
  top: 50px;
  left: 10px;
}
</style>
